import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { VoiceSamplePreview } from '@utils/types/organization/voice-profile.ts';
import { formatSelectionDuration } from '@utils/editors/voice-sample-ranges.ts';

interface Props {
  preview: VoiceSamplePreview;
}

/** Play back the exact bytes that will be enrolled.
 *
 * The sample stays in memory: the object URL is created from the in-memory
 * ArrayBuffer, is never written to storage or a download, and is revoked as
 * soon as the preview changes or the dialog closes. The server already sends
 * `Cache-Control: no-store, private`, so nothing persists on either side.
 *
 * `controlsList="nodownload"` keeps the browser from offering to save the
 * clip. Remote playback is disabled on the element itself in an effect, since
 * React does not type that attribute. */
export const VoiceSamplePreviewPlayer = ({ preview }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Create and release the URL in the same effect. StrictMode replays effects;
  // creating during render would leave playback pointing at a revoked URL.
  useEffect(() => {
    const element = audioRef.current;
    const objectUrl = URL.createObjectURL(
      new Blob([preview.bytes], { type: 'audio/wav' })
    );
    if (element) element.src = objectUrl;
    return () => {
      element?.pause();
      element?.removeAttribute('src');
      URL.revokeObjectURL(objectUrl);
    };
  }, [preview.bytes]);

  // Keep the clip off AirPlay/Cast targets. Not in React's typings, so set
  // it on the element directly.
  useEffect(() => {
    const element = audioRef.current as
      | (HTMLAudioElement & { disableRemotePlayback?: boolean })
      | null;
    if (element) element.disableRemotePlayback = true;
  }, []);

  return (
    <div
      className="space-y-2 rounded-md p-3"
      style={{ backgroundColor: getColour('secondLayer') }}
    >
      <Text as="p" size="xs" tone="muted">
        {t('voice-preview-duration', {
          duration: formatSelectionDuration(preview.durationMs)
        })}
      </Text>
      {/* A voice sample has no caption track: its content is the speech
          being verified, which the admin is listening to on purpose. */}
      <audio
        ref={audioRef}
        controls
        controlsList="nodownload"
        preload="metadata"
        className="w-full"
        aria-label={t('voice-preview-player-label')}
      />
      <Text as="p" size="xs" tone="muted">
        {t('voice-preview-not-stored')}
      </Text>
    </div>
  );
};
